/**
 * Lab Tests — the catalogue of tests a patient can actually book.
 *
 * This is the Patient app's Lab Tests screen: the tests themselves, from every
 * lab on the network, with each lab's own price beside its own name. The web
 * app used to open the lab *directory* here — a list of three labs — which
 * answers "who runs tests" rather than "what can I book, and for how much".
 * The directory is still one tap away.
 *
 * Everything on screen is published: `GET /lab/tests` and `GET /lab/packages`
 * for the catalogue, `GET /lab/orders` for what the patient's own doctor
 * asked for. A test two labs publish under the same name is comparable, and
 * the card says so with their lowest price; a test only one lab publishes is
 * simply that lab's.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type DoctorLabOrder, type LabCataloguePackage, type LabCatalogueTest } from '../../api';

const ACCENT = '#0891B2'; // the app's lab role accent

type Sort = 'recommended' | 'price' | 'rating';
type Collection = 'HOME' | 'LAB';

const rupees = (n: number) => `₹${n.toLocaleString('en-IN')}`;

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconFlask({ color, size = 22 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6M10 3v6.5L5.5 18a2 2 0 001.8 3h9.4a2 2 0 001.8-3L14 9.5V3" />
    </svg>
  );
}

function IconSearch({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function IconX({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function IconHome({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10l9-7 9 7v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><path d="M9 22V12h6v10" />
    </svg>
  );
}

function IconBuilding({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16M15 21V11h4a2 2 0 012 2v8" /><path d="M9 7h2M9 11h2M9 15h2" />
    </svg>
  );
}

function IconVerified({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill={color} stroke="none">
      <path d="M12 2l2.5 4.5L19 7.5l-3.5 3.5.8 5L12 14l-4.3 2 .8-5L5 7.5l4.5-1L12 2z" />
    </svg>
  );
}

function IconHeart({ filled, color }: { filled: boolean; color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21.2l7.7-7.7 1.1-1.1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}

function IconCompare({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 16H3l4-4M3 16l4 4M17 8h4l-4-4M21 8l-4 4" /><path d="M7 16h10M17 8H7" />
    </svg>
  );
}

function IconClipboard({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
    </svg>
  );
}

function IconReceipt({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 3v18l2-1.5L9 21l2-1.5L13 21l2-1.5L17 21l2-1.5V3l-2 1.5L15 3l-2 1.5L11 3 9 4.5 7 3z" /><path d="M9 8h6M9 12h6" />
    </svg>
  );
}

// ─── Small pieces ────────────────────────────────────────────────────────────

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: T.font, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
        color: active ? T.white : T.textSec,
        background: active ? ACCENT : T.white,
        border: `1.5px solid ${active ? ACCENT : T.border}`,
        borderRadius: 20, padding: '7px 13px', cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
      <style>{'@keyframes lab-spin { to { transform: rotate(360deg); } }'}</style>
      <div style={{ width: 34, height: 34, border: `3px solid ${T.border}`, borderTop: `3px solid ${ACCENT}`, borderRadius: '50%', animation: 'lab-spin 0.8s linear infinite' }} />
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function LabTestsCatalogue() {
  const navigate = useNavigate();

  const [tests, setTests] = useState<LabCatalogueTest[]>([]);
  const [packages, setPackages] = useState<LabCataloguePackage[]>([]);
  const [orders, setOrders] = useState<DoctorLabOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [collection, setCollection] = useState<Collection>('HOME');
  const [sort, setSort] = useState<Sort>('recommended');
  const [savedOnly, setSavedOnly] = useState(false);
  /** Saved tests live on this screen only — the server keeps no such list. */
  const [saved, setSaved] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await patientApi.lab.catalogue();
      setTests(Array.isArray(rows) ? rows : []);
    } catch {
      setTests([]);
      setError('Could not load the lab catalogue. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Packages and the doctor's own orders load alongside and degrade to
  // nothing: neither failing is a reason to withhold the catalogue.
  useEffect(() => {
    let alive = true;
    patientApi.lab.packageCatalogue()
      .then((rows) => { if (alive) setPackages(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setPackages([]); });
    patientApi.doctorLabOrders()
      .then((rows) => { if (alive) setOrders(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setOrders([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  /** How many labs publish a test under this exact name. */
  const labsPerName = useMemo(() => {
    const m = new Map<string, { count: number; lowest: number }>();
    for (const t of tests) {
      const key = t.name.trim().toLowerCase();
      const seen = m.get(key);
      if (seen) { seen.count += 1; seen.lowest = Math.min(seen.lowest, t.price); }
      else m.set(key, { count: 1, lowest: t.price });
    }
    return m;
  }, [tests]);

  /** Browse-by-category facets, built only from categories that really exist. */
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const t of tests) if (t.category) set.add(t.category);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [tests]);

  const visible = useMemo(() => {
    let rows = tests;
    // Home collection is the lab's own tag; nothing here decides it.
    if (collection === 'HOME') rows = rows.filter((t) => t.lab.homeCollection);
    if (category) rows = rows.filter((t) => t.category === category);
    if (savedOnly) rows = rows.filter((t) => saved.has(t.id));
    if (search) {
      rows = rows.filter((t) =>
        t.name.toLowerCase().includes(search)
        || (t.category ?? '').toLowerCase().includes(search)
        || t.lab.name.toLowerCase().includes(search));
    }
    const out = [...rows];
    if (sort === 'price') out.sort((a, b) => a.price - b.price);
    if (sort === 'rating') out.sort((a, b) => (b.lab.rating ?? -1) - (a.lab.rating ?? -1));
    return out;
  }, [tests, collection, category, savedOnly, saved, search, sort]);

  const showPackages = !category && !search && !savedOnly && packages.length > 0;

  const toggleSaved = (id: string) => setSaved((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div style={{ background: T.bg, fontFamily: T.font, margin: '0 auto', paddingBottom: 100 }}>

      {/* ── Header ───────────────────────────────────────────────── */}
      <div style={{ background: `linear-gradient(145deg, ${ACCENT} 0%, #0E7490 55%, ${T.navy} 100%)`, padding: '32px 16px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.75)' }}>
              NEOHEALTHCARD
            </div>
            <h1 style={{ fontFamily: T.font, fontSize: 22, fontWeight: 800, color: T.white, margin: '2px 0 0' }}>Lab Tests</h1>
            <p style={{ fontFamily: T.font, fontSize: 12.5, color: 'rgba(255,255,255,0.82)', margin: '3px 0 0' }}>
              Compare and book tests from verified labs
            </p>
          </div>
          <button
            onClick={() => navigate('/patient/lab-appointments')}
            title="My bookings"
            aria-label="My bookings"
            style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 11, padding: 8, cursor: 'pointer', display: 'flex' }}
          >
            <IconReceipt color={T.white} />
          </button>
        </div>

        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: T.white, borderRadius: 14, padding: '11px 14px', marginTop: 14, boxShadow: T.shadowMd }}>
          <IconSearch color={T.textTer} />
          <input
            type="text"
            placeholder="Search lab tests"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', fontFamily: T.font, fontSize: 13, color: T.text, background: 'transparent', padding: 0 }}
          />
          {searchInput && (
            <button onClick={() => setSearchInput('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <IconX color={T.textTer} />
            </button>
          )}
        </div>
      </div>

      {/* ── Collection mode ──────────────────────────────────────── */}
      <div style={{ padding: '12px 16px 0', display: 'flex', gap: 8 }}>
        {([['HOME', 'Home collection', IconHome], ['LAB', 'Visit a lab', IconBuilding]] as const).map(([mode, label, Icon]) => {
          const active = collection === mode;
          return (
            <button
              key={mode}
              onClick={() => setCollection(mode)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '9px 8px', borderRadius: 10, cursor: 'pointer',
                background: active ? ACCENT + '1F' : T.white,
                border: `1.5px solid ${active ? ACCENT : T.border}`,
                fontFamily: T.font, fontSize: 12, fontWeight: 800, color: active ? T.navy : T.textSec,
              }}
            >
              <Icon color={active ? ACCENT : T.textSec} />
              {label}
            </button>
          );
        })}
      </div>

      {/* Saved filter + the lab directory this screen replaced */}
      <div style={{ padding: '8px 16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Chip label={savedOnly ? 'Saved only' : 'Saved'} active={savedOnly} onClick={() => setSavedOnly((v) => !v)} />
        <span style={{ flex: 1 }} />
        <button
          onClick={() => navigate('/patient/care-services/labs/directory')}
          style={{
            fontFamily: T.font, fontSize: 12, fontWeight: 700, color: ACCENT,
            background: ACCENT + '12', border: `1px solid ${ACCENT}30`,
            borderRadius: 20, padding: '7px 13px', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Browse labs →
        </button>
      </div>

      <div style={{ padding: '14px 16px 0' }}>
        {loading ? <Spinner /> : error ? (
          <div style={{ textAlign: 'center', padding: '40px 16px' }}>
            <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 700, color: T.text }}>Could not load</div>
            <div style={{ fontFamily: T.font, fontSize: 12.5, color: T.textSec, marginTop: 6 }}>{error}</div>
            <button
              onClick={() => void load()}
              style={{ marginTop: 14, padding: '10px 18px', borderRadius: 10, background: ACCENT + '1A', border: `1px solid ${ACCENT}47`, cursor: 'pointer', fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.navy }}
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {/* ── What the patient's own doctor ordered ─────────── */}
            {orders.length > 0 && (
              <div style={{ background: `${T.navy}0A`, border: `1px solid ${T.navy}20`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <IconClipboard color={T.navy} />
                  <span style={{ fontFamily: T.font, fontSize: 14, fontWeight: 800, color: T.navy }}>Ordered by your doctor</span>
                </div>
                {orders.map((o) => {
                  const summary = o.tests.map((t) => t.name).filter(Boolean).join(', ');
                  const meta = [
                    o.doctorName,
                    o.priority ? o.priority.toUpperCase() : null,
                    o.orderedOn ? new Date(o.orderedOn).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null,
                    // Said only when the doctor said it — silence is not
                    // "no fasting needed".
                    o.tests.some((t) => t.fasting) ? 'Fasting required' : null,
                  ].filter(Boolean).join(' · ');
                  return (
                    <div key={o.id} style={{ marginBottom: 8 }}>
                      <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>{summary || 'Lab test'}</div>
                      {meta && <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, marginTop: 2 }}>{meta}</div>}
                      {o.clinicalNotes && <div style={{ fontFamily: T.font, fontSize: 11.5, color: T.textTer, marginTop: 2 }}>{o.clinicalNotes}</div>}
                    </div>
                  );
                })}
                <div style={{ fontFamily: T.font, fontSize: 11.5, color: T.textTer }}>
                  Search for these below to book them at a lab of your choice.
                </div>
              </div>
            )}

            {/* ── Browse by category ────────────────────────────── */}
            {categories.length > 0 && (
              <div style={{ overflowX: 'auto', paddingBottom: 4, marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
                  <Chip label="All" active={category === null} onClick={() => setCategory(null)} />
                  {categories.map((c) => (
                    <Chip key={c} label={c} active={category === c} onClick={() => setCategory(category === c ? null : c)} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Health packages ───────────────────────────────── */}
            {showPackages && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 800, color: T.navy, marginBottom: 8 }}>Health packages</div>
                <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
                  <div style={{ display: 'flex', gap: 10, width: 'max-content' }}>
                    {packages.map((p) => (
                      <div key={`${p.lab.id}-${p.id}`} style={{ width: 262, background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, boxSizing: 'border-box' }}>
                        <div style={{ fontFamily: T.font, fontSize: 13.5, fontWeight: 800, color: T.text }}>{p.name}</div>
                        <div style={{ fontFamily: T.font, fontSize: 11.5, color: T.textSec, marginTop: 3 }}>{p.lab.name}</div>
                        {p.includes.length > 0 && (
                          <div style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, marginTop: 6, lineHeight: 1.5 }}>
                            {p.includes.slice(0, 4).join(' · ')}
                            {p.includes.length > 4 ? ` +${p.includes.length - 4} more` : ''}
                          </div>
                        )}
                        {p.preparation && (
                          <div style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, marginTop: 6 }}>{p.preparation}</div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                          <span style={{ fontFamily: T.font, fontSize: 16, fontWeight: 800, color: T.navy }}>{rupees(p.price)}</span>
                          <button
                            onClick={() => navigate(`/patient/care-services/labs/${p.lab.slug}`)}
                            style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.white, background: ACCENT, border: 'none', borderRadius: 9, padding: '8px 16px', cursor: 'pointer' }}
                          >
                            Book
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Count + sort ──────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 800, color: T.navy }}>
                {visible.length} {visible.length === 1 ? 'test' : 'tests'}{category ? ` · ${category}` : ''}
              </span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as Sort)}
                style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.navy, background: T.white, border: `1.5px solid ${T.border}`, borderRadius: 8, padding: '5px 8px', cursor: 'pointer', outline: 'none' }}
              >
                <option value="recommended">Recommended</option>
                <option value="price">Lowest price</option>
                <option value="rating">Top rated</option>
              </select>
            </div>

            {/* ── Test cards ────────────────────────────────────── */}
            {visible.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 16px' }}>
                <div style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.text }}>
                  {savedOnly ? 'No saved tests' : 'No matches'}
                </div>
                <div style={{ fontFamily: T.font, fontSize: 12.5, color: T.textSec, marginTop: 6 }}>
                  {collection === 'HOME'
                    ? 'No lab publishing these tests offers home collection. Try “Visit a lab”.'
                    : 'Try a different category or search term.'}
                </div>
              </div>
            ) : visible.map((t) => {
              const key = t.name.trim().toLowerCase();
              const group = labsPerName.get(key);
              const labCount = group?.count ?? 1;
              const isSaved = saved.has(t.id);
              const meta = [t.category, t.sampleType].filter(Boolean).join(' · ');
              return (
                <div key={`${t.lab.id}-${t.id}`} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 13, marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, flexShrink: 0, background: ACCENT + '1F', display: 'grid', placeItems: 'center' }}>
                      <IconFlask color={ACCENT} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 600, color: T.text }}>{t.name}</div>
                      {meta && <div style={{ fontFamily: T.font, fontSize: 13.5, color: T.textSec, marginTop: 1 }}>{meta}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                        <span style={{ fontFamily: T.font, fontSize: 12.5, color: T.textTer, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.lab.name}
                        </span>
                        {t.lab.verified && <IconVerified color={ACCENT} />}
                      </div>
                      {/* Only ever what the lab published. */}
                      {(t.turnaroundHours || t.lab.homeCollection) && (
                        <div style={{ fontFamily: T.font, fontSize: 11.5, color: T.textTer, marginTop: 3 }}>
                          {[
                            t.turnaroundHours ? `Report in ${t.turnaroundHours} hours` : null,
                            t.lab.homeCollection ? 'Home collection' : null,
                          ].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                      {labCount > 1 && (
                        <span style={{ fontFamily: T.font, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: T.textTer }}>FROM</span>
                      )}
                      <span style={{ fontFamily: T.font, fontSize: 17, fontWeight: 700, color: T.navy }}>
                        {rupees(labCount > 1 ? (group?.lowest ?? t.price) : t.price)}
                      </span>
                      <button
                        onClick={() => toggleSaved(t.id)}
                        aria-label={isSaved ? `Remove ${t.name} from saved tests` : `Save ${t.name}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0 0', display: 'flex' }}
                      >
                        <IconHeart filled={isSaved} color={T.purple} />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                    {labCount > 1 && (
                      <button
                        onClick={() => navigate(`/patient/care-services/labs/compare?name=${encodeURIComponent(t.name)}`)}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.navy,
                          background: T.white, border: `1.5px solid ${T.border}`, borderRadius: 9, padding: '8px 0', cursor: 'pointer',
                        }}
                      >
                        <IconCompare color={T.navy} />
                        Compare {labCount}
                      </button>
                    )}
                    <span style={{ flex: 1 }} />
                    <button
                      onClick={() => navigate(`/patient/care-services/labs/${t.lab.slug}/book?test=${encodeURIComponent(t.name)}&collection=${collection}`)}
                      style={{
                        fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.white,
                        background: ACCENT, border: 'none', borderRadius: 9, padding: '8px 20px', cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      Book
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
